/**
 * App-level upload runs (§12 "Upload"). An {@link UploadTask} lives here, not in the Upload
 * page, so it keeps sending blocks while the user moves to other pages; the top bar shows
 * its progress (`layout/UploadIndicator`) and closing the tab mid-upload asks first. One
 * run per project at a time; finished results are kept for the page's ingest &
 * verification section until the user clears them or reloads.
 */
import { create } from 'zustand';
import {
  UploadAbortedError,
  UploadPausedError,
  UploadTask,
  type UploadProgress,
  type UploadTaskOptions,
} from '@/api/upload';
import type { UploadOptions, UploadResult } from '@/api/types';

export interface UploadRunState {
  projectId: number;
  /** Changes whenever a new task starts (React keys, stale callbacks). */
  runId: number;
  progress: UploadProgress | null;
  result: UploadResult | null;
  error: unknown;
}

interface UploadsState {
  runs: Record<number, UploadRunState>;
  /** Completed uploads per project, newest first (the page's ingest list). */
  finished: Record<number, UploadResult[]>;
}

export const useUploads = create<UploadsState>()(() => ({ runs: {}, finished: {} }));

const tasks = new Map<number, UploadTask>();
let runCounter = 0;

export function isUploadActive(progress: UploadProgress | null | undefined): boolean {
  const phase = progress?.phase;
  return phase === 'preparing' || phase === 'uploading' || phase === 'completing';
}

function patchRun(pid: number, runId: number, patch: Partial<UploadRunState>): void {
  useUploads.setState((s) => {
    const run = s.runs[pid];
    if (!run || run.runId !== runId) return s;
    return { runs: { ...s.runs, [pid]: { ...run, ...patch } } };
  });
}

export interface StartUploadArgs {
  projectId: number;
  files: readonly File[];
  options?: UploadOptions;
  resume?: UploadTaskOptions['resume'];
  /** After a successful upload (e.g. invalidate image lists). */
  onDone?: (result: UploadResult) => void;
  /** Tests: engine options. */
  task?: Partial<UploadTaskOptions>;
}

/** Starts (or restarts) the upload of a project; a running one is paused first. */
export function startUpload({
  projectId: pid,
  files,
  options,
  resume,
  onDone,
  task: extra,
}: StartUploadArgs): void {
  tasks.get(pid)?.pause();
  const runId = ++runCounter;
  const task = new UploadTask({
    ...extra,
    projectId: pid,
    files,
    options,
    resume,
    onProgress: (progress) => patchRun(pid, runId, { progress }),
  });
  tasks.set(pid, task);
  useUploads.setState((s) => ({
    runs: {
      ...s.runs,
      [pid]: { projectId: pid, runId, progress: task.getProgress(), result: null, error: null },
    },
  }));
  syncUnloadGuard();
  task.promise.then(
    (result) => {
      patchRun(pid, runId, { result });
      useUploads.setState((s) => ({
        finished: { ...s.finished, [pid]: [result, ...(s.finished[pid] ?? [])] },
      }));
      onDone?.(result);
    },
    (error: unknown) => {
      if (!(error instanceof UploadAbortedError) && !(error instanceof UploadPausedError))
        patchRun(pid, runId, { error });
    },
  );
  void task.promise.catch(() => undefined).finally(syncUnloadGuard);
}

export function pauseUpload(pid: number): void {
  tasks.get(pid)?.pause();
}

/** Cancels the upload (staged data is deleted on the server). */
export function abortUpload(pid: number): void {
  tasks.get(pid)?.abort();
}

/** Forgets the run of a project (back to the file list). */
export function clearUploadRun(pid: number): void {
  const task = tasks.get(pid);
  if (task && isUploadActive(task.getProgress())) return;
  tasks.delete(pid);
  useUploads.setState((s) => {
    if (!(pid in s.runs)) return s;
    const runs = { ...s.runs };
    delete runs[pid];
    return { runs };
  });
}

/** Adds results that did not come from a browser upload (server import) to the ingest list. */
export function addFinishedUpload(pid: number, result: UploadResult): void {
  useUploads.setState((s) => ({ finished: { ...s.finished, [pid]: [result, ...(s.finished[pid] ?? [])] } }));
}

/** Runs that are still sending (any project). */
export function activeUploadRuns(state: UploadsState = useUploads.getState()): UploadRunState[] {
  return Object.values(state.runs).filter((run) => isUploadActive(run.progress));
}

// ---------------------------------------------------------------------- tab close guard

const onBeforeUnload = (event: BeforeUnloadEvent) => {
  if (activeUploadRuns().length === 0) return;
  event.preventDefault(); // the upload stays resumable, but only while the tab is open
};
let guarding = false;

function syncUnloadGuard(): void {
  if (typeof window === 'undefined') return;
  const needed = [...tasks.values()].some((t) => isUploadActive(t.getProgress()));
  if (needed && !guarding) window.addEventListener('beforeunload', onBeforeUnload);
  if (!needed && guarding) window.removeEventListener('beforeunload', onBeforeUnload);
  guarding = needed;
}

/** Tests. */
export function resetUploads(): void {
  for (const task of tasks.values()) task.pause();
  tasks.clear();
  useUploads.setState({ runs: {}, finished: {} });
  syncUnloadGuard();
}
